import { VariantProps, cva } from "class-variance-authority";
import { LucideIcon } from "lucide-react";
import { forwardRef } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChatPing } from "@/features/chat/components/chat-ping";
import { cn } from "@/lib/utils";

const baseClasses = [
  "group flex h-full flex-col items-center justify-center py-2 px-4 transition-colors",
  "disabled:cursor-not-allowed disabled:hover:bg-background",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  "relative",
];

const actionButtonVariants = cva(baseClasses.join(" "), {
  variants: {
    variant: {
      default: "bg-background hover:bg-card w-[100px]",
      ghost: "bg-background hover:bg-card w-auto rounded-md",
      destructive: "bg-destructive/20 text-destructive hover:bg-destructive/30",
      "leave-call": "bg-[#F04438] text-white",
    },
    size: {
      fixed: "w-[100px]",
      auto: "min-w-16 p-2",
    },
    isActive: {
      true: "bg-card",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "fixed",
  },
});

const actionButtonIconVariants = cva("mb-1 size-5 transition-colors", {
  variants: {
    variant: {
      default:
        "text-foreground group-enabled:group-hover:text-primary group-disabled:text-white/50",
      ghost:
        "text-foreground group-enabled:group-hover:text-primary group-disabled:text-white/50",
      destructive:
        "text-destructive group-enabled:group-hover:text-destructive group-disabled:text-destructive/50",
      "leave-call": "text-white",
    },
    isActive: {
      true: "text-primary",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const actionButtonTextVariants = cva("text-nowrap text-xs", {
  variants: {
    variant: {
      default: "text-muted group-disabled:text-muted/50",
      ghost: "text-muted group-disabled:text-muted/50",
      destructive: "text-destructive group-disabled:text-destructive/50",
      "leave-call": "text-white",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface ActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof actionButtonVariants> {
  Icon?: LucideIcon;
  displayPing?: boolean;
  tooltipText?: string | React.ReactNode;
  text?: string;
  secondaryAction?: React.ReactNode;
}

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  (
    {
      Icon,
      className,
      variant,
      size,
      isActive,
      displayPing,
      tooltipText,
      text,
      secondaryAction,
      ...props
    },
    ref,
  ) => {
    return (
      <div className="action-button-container relative">
        <Tooltip delayDuration={700}>
          <TooltipTrigger asChild>
            <button
              ref={ref}
              className={cn(
                "action-button",
                actionButtonVariants({ variant, className, size, isActive }),
              )}
              aria-disabled={props.disabled}
              {...props}
            >
              {Icon && (
                <Icon
                  className={cn(
                    actionButtonIconVariants({ variant, isActive }),
                  )}
                />
              )}
              {text && (
                <span className={cn(actionButtonTextVariants({ variant }))}>
                  {text}
                </span>
              )}
              {displayPing && <ChatPing />}
            </button>
          </TooltipTrigger>
          {tooltipText && (
            <TooltipContent>
              <div>{tooltipText}</div>
            </TooltipContent>
          )}
        </Tooltip>
        {secondaryAction && (
          <div className="absolute right-0.5 top-0.5 z-10">
            {secondaryAction}
          </div>
        )}
      </div>
    );
  },
);

ActionButton.displayName = "ActionButton";
